import React, { Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { Environment, Network, Store, RecordSource } from 'relay-runtime'
import { lazy as loadable } from '@loadable/component'
import type { LoadableComponent } from '@loadable/component'

import { EmbedContext } from '@newComponents/Embed'
import { EmbedBrowserRouter } from '@newComponents/EmbedBrowserRouter'

import { Provider as DataProvider, createNetworkLayer } from '@data/Provider'

import { TamaguiProvider } from 'tamagui'
import tamaguiConfig from '../../config/tamagui.config'

import { HOST_NAME } from '@data/config/url'

import {
  transformCSSForShadowDOM,
  injectCSSIntoShadowRoot,
  injectPortalCSS,
  interceptHeadStyles,
} from './shadowDOMStyles'
import { EMBED_COMPONENT_CSS } from './embedComponentCSS'

// Import Tamagui extracted CSS as raw string for Shadow DOM injection
import tamaguiCSS from '../../config/tamagui.css?inline'

// Font-face declarations — imported normally so fonts load globally in <head>.
// @font-face is global: once loaded, fonts are available inside Shadow DOM too.
import './embedStyles.css'

// SDK-specific ErrorBoundary — avoids legacy Screen/TokensContext dependencies
class SDKErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  componentDidCatch(error: Error) {
    console.error('[Guild SDK]', error)
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 16, textAlign: 'center', fontFamily: 'Inter, sans-serif', color: '#666' }}>
          Something went wrong.
        </div>
      )
    }
    return this.props.children
  }
}

// SDK-specific Suspense wrapper — avoids legacy SuspenseWrapperLoadingSpinner
const wrapWithSuspense = <T extends {}>(Comp: LoadableComponent<T>) => {
  return (props: T) => (
    <Suspense fallback={null}>
      <Comp {...props} />
    </Suspense>
  )
}

const GuildCardEmbed = wrapWithSuspense(
  loadable(() => import('./Guild/GuildCardEmbed'))
)

const GuildEventLatest = wrapWithSuspense(
  loadable(() => import('./Guild/GuildEventLatest'))
)

const GuildEventList = wrapWithSuspense(
  loadable(() => import('./Guild/GuildEventList'))
)

const GuildPresentationLatest = wrapWithSuspense(
  loadable(() => import('./Guild/GuildPresentationLatest'))
)

const GuildPresentationList = wrapWithSuspense(
  loadable(() => import('./Guild/GuildPresentationList'))
)

const UserCardEmbed = wrapWithSuspense(
  loadable(() => import('./User/UserCardEmbed'))
)

const UserEventLatest = wrapWithSuspense(
  loadable(() => import('./User/UserEventLatest'))
)

const UserEventList = wrapWithSuspense(
  loadable(() => import('./User/UserEventList'))
)

const UserPresentationLatest = wrapWithSuspense(
  loadable(() => import('./User/UserPresentationLatest'))
)

const UserPresentationList = wrapWithSuspense(
  loadable(() => import('./User/UserPresentationList'))
)

const EventCardEmbed = wrapWithSuspense(
  loadable(() => import('./Event/EventCardEmbed'))
)

const EventItemEmbed = wrapWithSuspense(
  loadable(() => import('./Event/EventItemEmbed'))
)

const PresentationCardEmbed = wrapWithSuspense(
  loadable(() => import('./Presentation/PresentationCardEmbed'))
)

const PresentationItemEmbed = wrapWithSuspense(
  loadable(() => import('./Presentation/PresentationItemEmbed'))
)

const GRAPHQL_URL = `${HOST_NAME}/graphql`

let relayEnvironment: Environment | undefined

const renderToElement = (
  element: HTMLElement | null,
  render: () => JSX.Element
): (() => void) => {
  if (!element) {
    console.warn('Could not find HTML element to render SDK')
    return () => {}
  }

  // Create Shadow DOM for CSS isolation
  const shadowRoot = element.attachShadow({ mode: 'open' })

  // 1. Inject design system CSS (tokens, themes, base styles)
  const designSystemCSS = tamaguiConfig.getCSS()
  if (designSystemCSS) {
    injectCSSIntoShadowRoot(
      shadowRoot,
      transformCSSForShadowDOM(designSystemCSS)
    )
  }

  // 2. Inject extracted Tamagui component CSS
  // The ?inline import may resolve to a module object; extract the string.
  const tamaguiCSSString =
    typeof tamaguiCSS === 'string'
      ? tamaguiCSS
      : typeof (tamaguiCSS as any)?.default === 'string'
        ? (tamaguiCSS as any).default
        : undefined
  if (tamaguiCSSString) {
    injectCSSIntoShadowRoot(
      shadowRoot,
      transformCSSForShadowDOM(tamaguiCSSString)
    )
  }

  // 3. Inject companion CSS for newComponents used in embeds
  if (EMBED_COMPONENT_CSS) {
    injectCSSIntoShadowRoot(shadowRoot, EMBED_COMPONENT_CSS)
  }

  // 4. Inject design system CSS into document.head for portaled content (tooltips, popovers).
  //    Tamagui's Portal renders to document.body, outside the Shadow DOM. These portaled
  //    elements need access to CSS variable definitions (theme tokens) to render correctly.
  //    We inject both the design system CSS (raw token values like --c-grey0) AND the
  //    extracted Tamagui CSS (theme variable mappings like --grey0: var(--c-grey0)).
  const portalCSSSources = [designSystemCSS, tamaguiCSSString].filter(Boolean).join('\n')
  const cleanupPortalCSS = portalCSSSources ? injectPortalCSS(portalCSSSources) : () => {}

  // 5. Intercept all style injection into document.head (Tamagui, RNW, webpack chunks)
  const cleanupInterceptor = interceptHeadStyles(shadowRoot)

  // 6. Add theme/font classes to the host element so :host(.t_light) selectors match.
  //    transformCSSForShadowDOM converts `:root.t_light` → `:host(.t_light)`, which
  //    targets the host element — so it must carry the theme class.
  const themeClass = window.matchMedia('(prefers-color-scheme: dark)').matches ? 't_dark' : 't_light'
  element.classList.add(themeClass, 'font_body')

  // 7. Create render target inside shadow root
  const wrapper = document.createElement('div')
  wrapper.className = `${themeClass} font_body`
  shadowRoot.appendChild(wrapper)

  // 8. Create React root in Shadow DOM
  const root = createRoot(wrapper)

  // Cache the Relay Environment so everything in the SDK uses the same normalized cache
  if (!relayEnvironment) {
    relayEnvironment = new Environment({
      network: Network.create(
        createNetworkLayer(
          undefined,
          undefined,
          async () => undefined,
          GRAPHQL_URL
        )
      ),
      store: new Store(new RecordSource(), {
        gcReleaseBufferSize: 50,
        queryCacheExpirationTime: 60 * 60 * 1000,
      }),
    })
  }

  root.render(
    <TamaguiProvider
      config={tamaguiConfig}
      disableInjectCSS
      defaultTheme={window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'}
    >
      <DataProvider relayEnvironment={relayEnvironment}>
        <EmbedContext.Provider value={true}>
          <SDKErrorBoundary>
            <EmbedBrowserRouter forceExternalNavigation={true}>
              {render()}
            </EmbedBrowserRouter>
          </SDKErrorBoundary>
        </EmbedContext.Provider>
      </DataProvider>
    </TamaguiProvider>
  )

  return () => {
    cleanupPortalCSS()
    cleanupInterceptor()
    root.unmount()
  }
}

export const renderGuildCard = (
  element: HTMLElement,
  guildSlug: string,
  options: { expand?: boolean } = {}
) => {
  return renderToElement(element, () => (
    <GuildCardEmbed {...options} slug={guildSlug} />
  ))
}

export const renderGuildEventLatest = (
  element: HTMLElement,
  guildSlug: string,
  options: {
    showOrganizer?: boolean
    expand?: boolean
  } = {}
) => {
  return renderToElement(element, () => (
    <GuildEventLatest {...options} slug={guildSlug} />
  ))
}

export const renderGuildEventList = (
  element: HTMLElement,
  guildSlug: string,
  timeSelection: 'UPCOMING' | 'PAST',
  options: {
    itemGap?: number
    numberToDisplay?: number
    expand?: boolean
    itemsDisplayed?: (itemsDisplayed: number) => unknown
    hasMore?: (hasMore: boolean) => unknown
  } = {}
) => {
  return renderToElement(element, () => (
    <GuildEventList
      {...options}
      slug={guildSlug}
      timeSelection={timeSelection}
    />
  ))
}

export const renderGuildPresentationLatest = (
  element: HTMLElement,
  guildSlug: string,
  options: {
    expand?: boolean
  } = {}
) => {
  return renderToElement(element, () => (
    <GuildPresentationLatest {...options} slug={guildSlug} />
  ))
}

export const renderGuildPresentationList = (
  element: HTMLElement,
  guildSlug: string,
  timeSelection: 'UPCOMING' | 'OTHER',
  options: {
    itemGap?: number
    numberToDisplay?: number
    expand?: boolean
    itemsDisplayed?: (itemsDisplayed: number) => unknown
    hasMore?: (hasMore: boolean) => unknown
  } = {}
) => {
  return renderToElement(element, () => (
    <GuildPresentationList
      {...options}
      slug={guildSlug}
      timeSelection={timeSelection}
    />
  ))
}

export const renderUserCard = (
  element: HTMLElement,
  userSlug: string,
  options: { expand?: boolean } = {}
) => {
  return renderToElement(element, () => (
    <UserCardEmbed {...options} slug={userSlug} />
  ))
}

export const renderUserEventLatest = (
  element: HTMLElement,
  userSlug: string,
  options: {
    showOrganizer?: boolean
    expand?: boolean
  } = {}
) => {
  return renderToElement(element, () => (
    <UserEventLatest {...options} slug={userSlug} />
  ))
}

export const renderUserEventList = (
  element: HTMLElement,
  userSlug: string,
  timeSelection: 'UPCOMING' | 'PAST',
  options: {
    itemGap?: number
    numberToDisplay?: number
    expand?: boolean
    itemsDisplayed?: (itemsDisplayed: number) => unknown
    hasMore?: (hasMore: boolean) => unknown
  } = {}
) => {
  return renderToElement(element, () => (
    <UserEventList {...options} slug={userSlug} timeSelection={timeSelection} />
  ))
}

export const renderUserPresentationLatest = (
  element: HTMLElement,
  userSlug: string,
  options: {
    expand?: boolean
  } = {}
) => {
  return renderToElement(element, () => (
    <UserPresentationLatest {...options} slug={userSlug} />
  ))
}

export const renderUserPresentationList = (
  element: HTMLElement,
  userSlug: string,
  timeSelection: 'UPCOMING' | 'OTHER',
  options: {
    itemGap?: number
    numberToDisplay?: number
    expand?: boolean
    itemsDisplayed?: (itemsDisplayed: number) => unknown
    hasMore?: (hasMore: boolean) => unknown
  } = {}
) => {
  return renderToElement(element, () => (
    <UserPresentationList
      {...options}
      slug={userSlug}
      timeSelection={timeSelection}
    />
  ))
}

export const renderEventCard = (
  element: HTMLElement,
  eventSlug: string,
  options: { expand?: boolean } = {}
) => {
  return renderToElement(element, () => (
    <EventCardEmbed {...options} slug={eventSlug} />
  ))
}

export const renderEventItem = (
  element: HTMLElement,
  eventSlug: string,
  options: { expand?: boolean } = {}
) => {
  return renderToElement(element, () => (
    <EventItemEmbed {...options} slug={eventSlug} />
  ))
}

export const renderPresentationCard = (
  element: HTMLElement,
  presentationSlug: string,
  options: { expand?: boolean } = {}
) => {
  return renderToElement(element, () => (
    <PresentationCardEmbed {...options} slug={presentationSlug} />
  ))
}

export const renderPresentationItem = (
  element: HTMLElement,
  presentationSlug: string,
  options: { expand?: boolean } = {}
) => {
  return renderToElement(element, () => (
    <PresentationItemEmbed {...options} slug={presentationSlug} />
  ))
}

export default {
  renderGuildCard,
  renderGuildEventLatest,
  renderGuildEventList,
  renderGuildPresentationLatest,
  renderGuildPresentationList,
  renderUserCard,
  renderUserEventLatest,
  renderUserEventList,
  renderUserPresentationLatest,
  renderUserPresentationList,
  renderEventCard,
  renderEventItem,
  renderPresentationCard,
  renderPresentationItem,
}
